<template>
  <div>
    <!-- Street Address 1 -->
    <b-form-group label="Street Address *" label-for="af-street1">
      <b-form-input
        id="af-street1"
        v-model="internal.streetAddress1"
        :disabled="disabled"
        :state="errors.streetAddress1 ? false : null"
        @input="emitInput"
      />
      <b-form-invalid-feedback>{{ errors.streetAddress1 }}</b-form-invalid-feedback>
    </b-form-group>

    <!-- Street Address 2 -->
    <b-form-group label="Street Address 2" label-for="af-street2">
      <b-form-input
        id="af-street2"
        v-model="internal.streetAddress2"
        :disabled="disabled"
        @input="emitInput"
      />
    </b-form-group>

    <!-- City + State -->
    <b-row>
      <b-col md="6">
        <b-form-group label="City *" label-for="af-city">
          <b-form-input
            id="af-city"
            v-model="internal.city"
            :disabled="disabled"
            :state="errors.city ? false : null"
            @input="emitInput"
          />
          <b-form-invalid-feedback>{{ errors.city }}</b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label="State / Province *" label-for="af-state">
          <b-form-select
            v-if="useStateDropdown"
            id="af-state"
            v-model="internal.state"
            :options="filteredStateOptions"
            :disabled="disabled"
            :state="errors.state ? false : null"
            @change="emitInput"
          >
            <template #first>
              <b-form-select-option value="">Select state&hellip;</b-form-select-option>
            </template>
          </b-form-select>
          <b-form-input
            v-else
            id="af-state"
            v-model="internal.state"
            :disabled="disabled"
            :state="errors.state ? false : null"
            placeholder="State / Province / Region"
            @input="emitInput"
          />
          <b-form-invalid-feedback :force-show="!!errors.state">{{ errors.state }}</b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Postal Code + Country -->
    <b-row>
      <b-col md="6">
        <b-form-group label="Zip / Postal Code *" label-for="af-postal">
          <b-form-input
            id="af-postal"
            v-model="internal.postalCode"
            :disabled="disabled"
            :state="errors.postalCode ? false : null"
            @input="emitInput"
          />
          <b-form-invalid-feedback>{{ errors.postalCode }}</b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label="Country *" label-for="af-country">
          <b-form-select
            id="af-country"
            v-model="internal.country"
            :options="countryOptions"
            :disabled="disabled"
            :state="errors.country ? false : null"
            @change="onCountryChange"
          >
            <template #first>
              <b-form-select-option value="">Select country&hellip;</b-form-select-option>
            </template>
          </b-form-select>
          <b-form-invalid-feedback :force-show="!!errors.country">{{ errors.country }}</b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
export default {
  name: 'AddressForm',
  props: {
    value: { type: Object, default: () => ({}) },
    stateOptions: { type: Array, default: () => [] },
    countryOptions: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false }
  },
  data () {
    return {
      internal: {
        streetAddress1: '',
        streetAddress2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'USA'
      },
      errors: {
        streetAddress1: null,
        city: null,
        state: null,
        postalCode: null,
        country: null
      }
    }
  },
  computed: {
    useStateDropdown () {
      return this.filteredStateOptions.length > 0
    },
    filteredStateOptions () {
      return this.stateOptions.filter(s => s.country === this.internal.country)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (val) {
        this.syncFromValue(val)
      }
    },
    // Re-normalize state once options arrive (handles stored full names like "California" → "CA")
    stateOptions (newOpts) {
      if (newOpts && newOpts.length > 0 && this.internal.state) {
        const normalized = this.normalizeState(this.internal.state)
        if (normalized !== this.internal.state) {
          this.internal.state = normalized
          this.emitInput()
        }
      }
    },
    // Re-normalize country once options arrive (handles stored display names like "United States" → "USA")
    countryOptions (newOpts) {
      if (newOpts && newOpts.length > 0 && this.internal.country) {
        const normalized = this.normalizeCountry(this.internal.country)
        if (normalized !== this.internal.country) {
          this.internal.country = normalized
          this.emitInput()
        }
      }
    }
  },
  methods: {
    syncFromValue (val) {
      if (!val) {
        this.internal = { streetAddress1: '', streetAddress2: '', city: '', state: '', postalCode: '', country: 'USA' }
        return
      }
      this.internal = {
        streetAddress1: val.streetAddress1 || '',
        streetAddress2: val.streetAddress2 || '',
        city: val.city || '',
        state: this.normalizeState(val.state || ''),
        postalCode: val.postalCode || '',
        country: this.normalizeCountry(val.country)
      }
    },
    normalizeCountry (raw) {
      // null/empty → default to USA
      if (!raw) return 'USA'
      raw = raw.trim()
      if (!raw) return 'USA'
      // Options not loaded yet — pass raw through; stateOptions watcher will re-normalize
      if (!this.countryOptions || this.countryOptions.length === 0) return raw
      // Exact match on value (e.g. 'USA')
      const exact = this.countryOptions.find(c => c.value === raw)
      if (exact) return exact.value
      // Match by display text (e.g. 'United States')
      const byText = this.countryOptions.find(c => c.text && c.text.toLowerCase() === raw.toLowerCase())
      if (byText) {
        console.warn(`[AddressForm] Normalized country "${raw}" → "${byText.value}"`)
        return byText.value
      }
      console.warn(`[AddressForm] Unrecognized country "${raw}", defaulting to blank`)
      return ''
    },
    normalizeState (raw) {
      if (!raw) return ''
      raw = raw.trim()
      if (!raw) return ''
      // Options not loaded yet — pass raw through; stateOptions watcher will re-normalize
      if (!this.stateOptions || this.stateOptions.length === 0) return raw
      // Exact match on abbreviation value
      const exact = this.stateOptions.find(s => s.value === raw)
      if (exact) return exact.value
      // Match by full name (text field)
      const byName = this.stateOptions.find(s => s.text && s.text.toLowerCase() === raw.toLowerCase())
      if (byName) {
        console.warn(`[AddressForm] Normalized state "${raw}" → "${byName.value}"`)
        return byName.value
      }
      console.warn(`[AddressForm] Unrecognized state "${raw}", defaulting to blank`)
      return ''
    },
    onCountryChange (newCountry) {
      // Smart clear: only wipe state if current value is not valid in the new country's list
      if (this.internal.state) {
        const stillValid = this.stateOptions
          .filter(s => s.country === newCountry)
          .some(s => s.value === this.internal.state)
        if (!stillValid) {
          this.internal.state = ''
        }
      }
      this.emitInput()
    },
    emitInput () {
      this.$emit('input', { ...this.internal })
    },
    validate () {
      this.errors = { streetAddress1: null, city: null, state: null, postalCode: null, country: null }
      let valid = true
      if (!(this.internal.streetAddress1 || '').trim()) {
        this.errors.streetAddress1 = 'Street address is required.'
        valid = false
      }
      if (!(this.internal.city || '').trim()) {
        this.errors.city = 'City is required.'
        valid = false
      }
      if (!(this.internal.state || '').trim()) {
        this.errors.state = 'State / Province is required.'
        valid = false
      }
      if (!(this.internal.postalCode || '').trim()) {
        this.errors.postalCode = 'Zip / Postal Code is required.'
        valid = false
      }
      if (!(this.internal.country || '').trim()) {
        this.errors.country = 'Country is required.'
        valid = false
      }
      return valid
    },
    // Called by parent to surface API-level 400 field errors onto the form
    setApiErrors (message) {
      this.errors = { streetAddress1: null, city: null, state: null, postalCode: null, country: null }
      if (!message) return
      const lower = message.toLowerCase()
      if (lower.includes('street')) this.errors.streetAddress1 = 'Street address is required.'
      if (lower.includes('city')) this.errors.city = 'City is required.'
      if (lower.includes('state')) this.errors.state = 'State / Province is required.'
      if (lower.includes('postal') || lower.includes('zip')) this.errors.postalCode = 'Zip / Postal Code is required.'
      if (lower.includes('country')) this.errors.country = 'Country is required.'
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/shared/AddressForm.vue